import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Stack,
} from '@mui/material';

import { apiService } from '../services/api';
import { InternalOrder } from '../types';
import { apiErrorMessage } from '../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (order: InternalOrder) => void;
}

export default function CreateInternalOrderDialog({ open, onClose, onCreated }: Props) {
  const [auftragsnummer, setAuftragsnummer] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setAuftragsnummer('');
    setNotes('');
    setSubmitting(false);
    setError(null);
  }

  function handleClose() {
    if (submitting) return;
    reset();
    onClose();
  }

  async function handleSubmit() {
    const trimmed = auftragsnummer.trim();
    if (!trimmed) {
      setError('Auftragsnummer ist erforderlich.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const order = await apiService.createInternalOrder({
        auftragsnummer: trimmed,
        notes: notes.trim() || undefined,
      });
      onCreated(order);
      reset();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Auftrag konnte nicht angelegt werden.'));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Neuer Auftrag</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <TextField
            label="Auftragsnummer"
            value={auftragsnummer}
            onChange={(e) => setAuftragsnummer(e.target.value)}
            disabled={submitting}
            autoFocus
            required
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />
          <TextField
            label="Notizen (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            multiline
            minRows={2}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !auftragsnummer.trim()}
        >
          {submitting ? 'Anlegen…' : 'Anlegen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
